import { Alert, Box, Card, CardContent, CardHeader, CircularProgress, Divider, FormControl, Link, styled, Typography, TextField, Stack, Button, IconButton, Input, InputAdornment, InputLabel, Tooltip, LinearProgressProps, LinearProgress, FormHelperText, CardActions } from '@mui/material';
import { orange } from '@mui/material/colors';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

interface LinearProgressWithLabelProps {
    level: 'Poor' | 'Weak' | 'Strong' | 'Very Strong';
    value: number;
}

function LinearProgressWithLabel(props: LinearProgressWithLabelProps) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Box sx={{ width: '80%', mr: 1}}>
          <LinearProgress variant="determinate" value={props.value} color={props.level === 'Poor' ? 'error' : props.level === 'Weak' ? 'warning' : props.level === 'Strong' ? 'primary' : 'success'} />
        </Box>
        <Box sx={{ minWidth: 35, mt: 1.5 }}>
          <Typography
            variant="body2"
            sx={{ color: props.level === 'Poor' ? 'error.main' : props.level === 'Weak' ? 'warning.main' : props.level === 'Strong' ? 'primary.main' : 'success.main', fontSize: 'clamp(0.6rem, 10vw, 0.7rem)', mt: -2 }}
          >{props.level}</Typography>
        </Box>
      </Box>
    );
  }

const UpdatePasswordContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '30%',
    margin: '0 auto',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0,
      backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor:
        'radial-gradient(ellipse at 50% 50%, hsl(211, 89.70%, 7.60%), hsl(0, 11.10%, 10.60%))',
      ...theme.applyStyles('dark', {
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
          backgroundColor:
              'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
      }),
    },
  }));

interface PasswordResetConfig {
    resetToken: string;
    tokenError: string;
    isPasswordReset: boolean;
}

declare global {
    interface Window {
        passwordResetConfig?: PasswordResetConfig;
    }
}

export default function UpdatePassword() {
    const passwordResetConfig = window.passwordResetConfig ?? {
        resetToken: '',
        tokenError: '',
        isPasswordReset: false,
    };
    const isPasswordReset = passwordResetConfig.isPasswordReset;
    const tokenError = passwordResetConfig.tokenError;
    const resetToken = passwordResetConfig.resetToken;
    const [loading, setLoading] = React.useState(false);
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState({
        'current-password': '',
        'new-password': '',
        'confirm-password': ''
    });
    const [showPassword, setShowPassword] = React.useState({
        'current-password-adornment': false,
        'new-password-adornment': false,
        'confirm-password-adornment': false
    });

    const handleClickShowPassword = (id: keyof typeof showPassword) => setShowPassword((prevState) => ({ ...prevState, [id]: !prevState[id] }));
    const handleMouseDownPassword = (id: keyof typeof showPassword) => (event: React.MouseEvent<HTMLButtonElement>) => { event.preventDefault(); };
    const handleMouseUpPassword = (id: keyof typeof showPassword) => (event: React.MouseEvent<HTMLButtonElement>) => { event.preventDefault(); };
    const definePasswordStrength = (id: string) => {
        const password = document.getElementById(id) as HTMLInputElement;
        const criteria = [
            /[a-z]/.test(password?.value),
            /[A-Z]/.test(password?.value),
            /[0-9]/.test(password?.value),
            /[^a-zA-Z0-9\s]/g.test(password?.value)
        ]

        const meetingCriteriaCount = criteria.filter(Boolean).length;

        if (password?.value?.length === 0) {
            return <></>
        }
        else if (password?.value?.length > 0 && password?.value?.length < 10) {
            return <LinearProgressWithLabel level="Poor" value={25} />
        }
        else if (password?.value?.length >= 8) {
            switch (meetingCriteriaCount) {
                case 0:
                    return <LinearProgressWithLabel level="Poor" value={25} />
                case 1:
                    return <LinearProgressWithLabel level="Weak" value={50} />
                case 2:
                    return <LinearProgressWithLabel level="Weak" value={50} />
                case 3:
                    return <LinearProgressWithLabel level="Strong" value={75} />
                case 4:
                    return <LinearProgressWithLabel level="Very Strong" value={100} />
            }
        }
    }

    const handleSubmit =async (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (tokenError) {
            return;
        }
        if (!isPasswordReset && currentPassword.length === 0) {
            setError((prevState) => ({ ...prevState, 'current-password': 'The current password is required.' }));
            return;
        }
        if (newPassword.length === 0) {
            setError((prevState) => ({ ...prevState, 'new-password': 'The new password is required.' }));
            return;
        }
        if (confirmPassword.length === 0) {
            setError((prevState) => ({ ...prevState, 'confirm-password': 'The confirmed password is required.' }));
            return;
        }
        if (currentPassword === newPassword && !isPasswordReset) {
            setError((prevState) => ({ ...prevState, 'new-password': 'The new password cannot be the same as the current password.' }));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError((prevState) => ({ ...prevState, 'confirm-password': 'The new password and the confirmed password do not match.' }));
            return;
        }
    
        if ((isPasswordReset || error['current-password'] === '') && error['new-password'] === '' && error['confirm-password'] === '') {
            setLoading(true);
            const res = await axios.get('/csrf');
            axios.defaults.headers.post['xsrfCookieName'] = 'csrftoken';
            axios.defaults.headers.post['X-CSRFToken'] = res.data.csrfToken;
            axios.defaults.headers.post['Content-Type'] = 'application/json';
            const url = isPasswordReset ? `/reset-password/${resetToken}/` : window.location.pathname;
            const payload = isPasswordReset
                ? { new_password: newPassword, confirm_password: confirmPassword }
                : {
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                };
            axios.post(url, payload)
            .then(response => {
                if (response.data.message) {
                    window.location.href = response.data.url || (isPasswordReset ? '/login' : '/mfa');
                }
            })
            .catch(error => {
                setLoading(false);
                setTimeout(() => {
                    toast.error(error.response?.data?.message, {position: 'top-center'});
                }, 100);
            });
        }
    }

    return (
        <UpdatePasswordContainer direction="column" justifyContent="space-between">
            <Card variant="outlined" style={{ borderRadius: 5, width: '100%' }}>
                <CardHeader
                    title={
                        <Stack direction="column" alignItems="center" justifyContent="center" fontFamily='inherit' spacing={1}>
                            <img src="https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png" width="10%" height="10%" alt="IDEA Data Whispers" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'Trebuchet MS, sans-serif', maxWidth: '90%', textAlign: 'center', paddingTop: '5px', fontSize: 'clamp(1.1rem, 10vw, 1.2rem)' }}>
                            <Link href="/" style={{ textDecoration: 'none', color: 'inherit', fontFamily: 'Trebuchet MS, sans-serif' }}>IDEA Data Whispers</Link>
                            </Typography>
                        </Stack>
                    }
                    subheader={
                        <Typography variant="h5" color='text.secondary' sx={{ mt: 2, mb: -2, fontWeight: 'normal', textAlign: 'center', justifySelf: 'center', fontFamily: 'Trebuchet MS, sans-serif', justifyContent: 'center', fontSize: '0.9rem' }}>
                            {isPasswordReset
                                ? 'Use the form below to set a new password for your account.'
                                : "This is the first time you're logging in, and changing your password is a required security measure."}
                        </Typography>
                    }
                />
                <Divider sx={{ width: '100%', margin: 0, padding: 1 }} />
                {tokenError ? (
                    <Alert severity="error" sx={{ width: '100%', margin: '0 0 20px 0' }}>
                        {tokenError}
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ width: '100%', margin: '0 0 20px 0' }}>All fields are <b>required</b>.</Alert>
                )}
                <CardContent sx={{ padding: 2, marginTop: 2, justifyContent: 'center'}}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mt: -4, fontWeight: 'bold', fontFamily: 'Trebuchet MS, sans-serif' }}>
                        {isPasswordReset ? 'Reset Password' : 'Update Password'}
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', width: '100%', flexDirection: 'column', gap: 0, '& > :not(style)': { m: 1 } }}>
                        {!isPasswordReset && (
                        <FormControl 
                            fullWidth 
                            variant="standard"
                            error={error['current-password'] !== ''}
                            sx= {{ m: 1, py: 1 }}
                        >
                            <InputLabel htmlFor="current-password" required>Current Password</InputLabel>
                            <Input
                                id="current-password"
                                name="current-password"
                                type={showPassword['current-password-adornment'] ? 'text' : 'password'}
                                size="small"
                                sx={{
                                    fontSize: 'clamp(0.6rem, 10vw, 0.7rem)'
                                }}
                                value={currentPassword}

                                onChange={(event) => {setCurrentPassword(event.target.value); setError((prevState) => ({ ...prevState, 'current-password': '' }));}}
                                endAdornment={
                                    <InputAdornment id="current-password-adornment" position="end">
                                        <Tooltip title={showPassword['current-password-adornment'] ? 'Hide password' : 'Show password'}>
                                            <IconButton
                                                aria-label={
                                                    showPassword['current-password-adornment'] ? 'Hide password' : 'Show password'
                                                }
                                                onClick={() => handleClickShowPassword('current-password-adornment')}
                                                onMouseDown={() => handleMouseDownPassword('current-password-adornment')}
                                                onMouseUp={() => handleMouseUpPassword('current-password-adornment')}
                                            >
                                                {showPassword['current-password-adornment'] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            />
                            {currentPassword.length > 0 ? definePasswordStrength("current-password") : <></>}
                            {error['current-password'] !== '' && <FormHelperText error sx={{ mt: 1 }}>{error['current-password']}</FormHelperText>}
                        </FormControl>
                        )}
                        <FormControl 
                            fullWidth 
                            sx= {{ m: 1, py: 1 }} 
                            variant="standard"
                            error={error['new-password'] !== ''}
                        >
                            <InputLabel htmlFor="new-password" required>New Password</InputLabel>
                            <Input
                                id="new-password"
                                name="new-password"
                                type={showPassword['new-password-adornment'] ? 'text' : 'password'}
                                size="small"
                                sx={{
                                    fontSize: 'clamp(0.6rem, 10vw, 0.7rem)'
                                }}
                                value={newPassword}
                                onChange={(event) => {setNewPassword(event.target.value); setError((prevState) => ({ ...prevState, 'new-password': '' }));}}
                                endAdornment={
                                    <InputAdornment id="new-password-adornment" position="end">
                                        <Tooltip title={showPassword['new-password-adornment'] ? 'Hide password' : 'Show password'}>
                                            <IconButton
                                                aria-label={
                                                    showPassword['new-password-adornment'] ? 'Hide password' : 'Show password'
                                                }
                                                onClick={() => handleClickShowPassword('new-password-adornment')}
                                                onMouseDown={() => handleMouseDownPassword('new-password-adornment')}
                                                onMouseUp={() => handleMouseUpPassword('new-password-adornment')}
                                            >
                                                {showPassword['new-password-adornment'] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            />
                            {newPassword.length > 0 ? definePasswordStrength("new-password") : <></>}
                            {error['new-password'] !== '' && <FormHelperText error sx={{ mt: 1 }}>{error['new-password']}</FormHelperText>}
                        </FormControl>
                        <FormControl 
                            fullWidth 
                            error={confirmPassword.length > 0 ? newPassword !== confirmPassword : error['confirm-password'] !== ''} 
                            sx= {{ m: 1, py: 1 }} 
                            variant="standard"
                        >
                            <InputLabel htmlFor="confirm-password" required>Confirm Password</InputLabel>
                            <Input
                                id="confirm-password"
                                name="confirm-password"
                                type={showPassword['confirm-password-adornment'] ? 'text' : 'password'}
                                size="small"
                                sx={{
                                    fontSize: 'clamp(0.6rem, 10vw, 0.7rem)'
                                }}
                                value={confirmPassword}
                                onChange={(event) => {setConfirmPassword(event.target.value); setError((prevState) => ({ ...prevState, 'confirm-password': '' }));}}
                                endAdornment={
                                    <InputAdornment id="confirm-password-adornment" position="end">
                                        <Tooltip title={showPassword['confirm-password-adornment'] ? 'Hide password' : 'Show password'}>
                                            <IconButton
                                                aria-label={
                                                    showPassword['confirm-password-adornment'] ? 'Hide password' : 'Show password'
                                                }
                                                onClick={() => handleClickShowPassword('confirm-password-adornment')}
                                                onMouseDown={() => handleMouseDownPassword('confirm-password-adornment')}
                                                onMouseUp={() => handleMouseUpPassword('confirm-password-adornment')}
                                            >
                                                {showPassword['confirm-password-adornment'] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            />
                            {
                                confirmPassword.length > 0 && newPassword !== confirmPassword ? 
                                <FormHelperText error sx={{ mt: 1 }}>The new password and the confirmed password do not match</FormHelperText> : <></>
                            }
                            {error['confirm-password'] !== '' && <FormHelperText error sx={{ mt: 1 }}>{error['confirm-password']}</FormHelperText>}
                        </FormControl>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            fullWidth
                            loading={loading}
                            loadingPosition="end"
                            disabled={Boolean(tokenError) || loading}
                            loadingIndicator={
                                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                    {isPasswordReset ? 'Resetting...' : 'Updating...'}
                                    <CircularProgress variant="indeterminate" size={16} sx={{ color: 'white' }} />
                                </Typography>
                            }
                            style={{
                                textTransform: 'none',
                                backgroundColor: orange[800],
                                fontWeight: isPasswordReset ? 'normal' : 'bold',
                                width: '100%',
                                margin: '0 auto',
                                height: 50,
                                border: 0,
                                boxShadow: 'none',
                                fontSize: 'clamp(1.1rem, 10vw, 1rem)',
                            }}
                        >
                            {!loading && (isPasswordReset ? 'Reset Password' : 'Update Password')}
                        </Button>
                        {tokenError && (
                            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                                <Link href="/login">Return to sign in</Link> to request a new reset link.
                            </Typography>
                        )}
                    </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center' }}>
                    <Typography id="copyright-text" variant="h6" sx={{ fontFamily: 'inherit', textAlign: 'center', width: '100%', fontSize: '0.8rem', marginBottom: 2 }}>
                        {`© ${new Date().getFullYear()}, All rights reserved by IDEA`}
                    </Typography>
                </CardActions>
            </Card>
        </UpdatePasswordContainer>
    );
}